import { createStyles, makeStyles } from '@material-ui/styles';
import { Grid, Theme, Typography } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { push } from 'connected-react-router';

import { selectPasswordRecoveryForm } from '../../../store/modules/password-recovery/selectors';
import { ExclamationCircleIcon } from '../../../components/icon/solid';
import { Link, PagesTitle, Button } from '../../../components';
import { useTitle } from '../../../hooks';
import { resetForm } from '../../../store/modules/password-recovery/actions';
import { Links } from '../../../utility/links';
import PageContainer from '../PageContainer';

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  icon: {
    height: '64px',
    width: '64px',
    color: palette.error.main,
  },
  title: {
    marginTop: '56px',
    marginBottom: '24px',
  },
  email: {
    fontWeight: 600,
  },
  body: {
    color: palette.text.secondary,
    textAlign: 'center',
    fontSize: '16px',
    paddingBottom: '16px',

    '& p': {
      marginBlockStart: 0,
      marginBlockEnd: 0,
    },
  },
  button: {
    borderRadius: '5px',
    borderWidth: '1.5px',
    minWidth: '170px',

    fontFamily: 'Montserrat',
    fontSize: '16px',
    fontWeight: 600,
    textTransform: 'none',
    margin: '42px 12px 42px 12px',
  },
  buttonLink: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
  support: {
    color: palette.menuAction.selected,
    borderColor: palette.menuAction.selected,
    '&:hover': {
      backgroundColor: palette.buttonSingle.hover,
    },
  },
  retry: {
    '&:hover': {
      borderWidth: '1.5px',
    },
  },
}), { name: 'PasswordRecovery' });

const PasswordRecoveryError = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  useTitle('Password Reset Error');

  const { email } = useSelector(selectPasswordRecoveryForm);
  useEffect(() => {
    if (!email) {
      dispatch(push('/'));
    }
  }, [dispatch, email]);

  const againHandler = useCallback(() => {
    dispatch(resetForm());
    return () => dispatch(push('/pages/password-recover'));
  }, [dispatch]);

  return (
    <PageContainer>
      <Grid container className={classes.root}>
        <ExclamationCircleIcon className={classes.icon} />
        <PagesTitle className={classes.title}>Password Reset Error</PagesTitle>
        <Typography variant="body1" component="div" className={classes.body}>
          <p>An error has occured when </p>
          <p>attempting to recover the password for:</p>
          <p className={classes.email}>{email}</p>
        </Typography>
        <Typography variant="body1" component="div" className={classes.body}>
          <p>Please try again or contact customer support.</p>
        </Typography>
        <Grid item xs={12}>
          <Link to={Links.support} defaultAction target="_blank" className={classes.buttonLink}>
            <Button
              variant="outlined"
              className={clsx(classes.button, classes.support)}
              overlay={false}
            >
              Customer Support
            </Button>
          </Link>
          <Link to="/pages/password-recover" className={classes.buttonLink}>
            <Button
              onClick={againHandler}
              className={clsx(classes.button, classes.retry)}
              variant="outlined"
              color="primary"
              overlay={false}
            >
              Try Again
            </Button>
          </Link>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default PasswordRecoveryError;
